import React from "react";
import { Link } from "react-router-dom";
import {
  Stethoscope, FlaskConical, Users, Car, HeartHandshake, Calendar,
  ArrowRight, ShieldCheck, Sparkles, Building2,
} from "lucide-react";

/** Nova Home Filho Presente — hero com foto de família + 6 cards de serviços.
 * Design inspirado na referência enviada pelo usuário: navy solid,
 * headline pesada em branco/âmbar, foto acolhedora à direita. */

const SERVICE_CARDS = [
  { icon: Stethoscope, title: "Consultas", desc: "Encontre e agende com facilidade", to: "/filho-presente/solicitar-consulta", color: "text-sky-300" },
  { icon: FlaskConical, title: "Exames", desc: "Solicite e acompanhe seus exames", to: "/filho-presente/solicitar-exame", color: "text-orange-300" },
  { icon: Users, title: "Profissionais", desc: "Diversas especialidades para todas as necessidades", to: "/filho-presente/profissionais", color: "text-emerald-300" },
  { icon: Building2, title: "Clínicas & ILPIs", desc: "Casas de repouso, consultórios e centros-dia", to: "/filho-presente/clinicas", color: "text-amber-300" },
  { icon: Car, title: "Transporte", desc: "Mais segurança para chegar até lá", to: "/filho-presente/solicitar-transporte", color: "text-purple-300" },
  { icon: HeartHandshake, title: "Acompanhamento", desc: "Alguém cuidando de cada detalhe", to: "/filho-presente/solicitar-acompanhante", color: "text-rose-300" },
  { icon: Calendar, title: "Agenda", desc: "Tudo organizado em um só lugar", to: "/filho-presente/agenda", color: "text-sky-300" },
];

export default function FilhoPresenteHome() {
  return (
    <div className="min-h-[70vh] bg-white" data-testid="fp-home">
      {/* HERO — foto full-bleed + gradiente azul à esquerda */}
      <section className="relative bg-[#0B2B62] text-white overflow-hidden">
        {/* Foto de fundo, ocupa todo o hero */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://customer-assets-4nw71qhi.emergentagent.net/job_internacao-segura/artifacts/auoc3ewq_ChatGPT%20Image%201%20de%20set.%20de%202026%2C%2019_13_24.png')",
            backgroundPosition: "center right",
          }}
          aria-hidden
        />
        {/* Gradiente azul: sólido na esquerda → transparente na direita para revelar a foto */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-[#0B2B62] from-30% via-[#0B2B62]/85 via-55% to-transparent to-90%"
          aria-hidden
        />
        {/* Vinheta suave no topo/base para consistência */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B2B62]/30 via-transparent to-[#0B2B62]/40" aria-hidden />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 lg:pt-16 pb-40 sm:pb-44 lg:pb-48">
          {/* Marca "Filho Presente" grande, no topo do hero */}
          <div className="inline-flex items-center gap-3 mb-14 sm:mb-16 lg:mb-20">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-amber-400/15 border border-amber-300/40 flex items-center justify-center">
              <HeartHandshake className="w-7 h-7 sm:w-8 sm:h-8 text-amber-300" strokeWidth={1.6} />
            </div>
            <div className="leading-tight">
              <div className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">Filho Presente</div>
              <div className="text-xs sm:text-sm text-amber-200/90 font-medium">by RAF Direct</div>
            </div>
          </div>

          {/* Texto + CTAs sobre a parte azul */}
          <div className="max-w-xl lg:max-w-2xl">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-white">
              Mesmo quando você não pode estar perto,
              <br />
              <span className="text-amber-300">o cuidado pode estar.</span>
            </h1>
            <p className="text-base sm:text-lg text-white/85 mt-6 max-w-lg leading-relaxed">
              Encontre profissionais, organize consultas, exames e cuidados de quem você ama — em um só lugar.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link to="/filho-presente/painel" data-testid="fp-cta-start"
                className="inline-flex items-center justify-center gap-2 bg-amber-400 text-[#0B2B62] font-semibold px-6 py-3.5 rounded-lg hover:bg-amber-300 transition-colors shadow-lg shadow-amber-500/20">
                Começar agora <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/filho-presente/profissionais" data-testid="fp-cta-find-pro"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white/40 text-white font-medium px-6 py-3.5 rounded-lg hover:bg-white/10 transition-colors">
                Encontrar profissional
              </Link>
              <Link to="/filho-presente/clinicas" data-testid="fp-cta-find-clinic"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-amber-300/60 text-amber-100 font-medium px-6 py-3.5 rounded-lg hover:bg-amber-300/10 transition-colors">
                <Building2 className="w-4 h-4" /> Encontrar clínica / ILPI
              </Link>
            </div>
            <div className="flex items-center gap-2 mt-6 text-xs text-white/60">
              <ShieldCheck className="w-3.5 h-3.5" /> Dados protegidos — conformidade LGPD
            </div>
          </div>
        </div>

        {/* Faixa de cards de serviços — flutuando na base do hero */}
        <div className="absolute inset-x-0 bottom-0 pb-6 sm:pb-8 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2.5 sm:gap-3">
              {SERVICE_CARDS.map((c) => (
                <Link key={c.to} to={c.to} data-testid={`fp-svc-${c.to.split("/").pop()}`}
                  className="group relative bg-[#0B2B62]/60 backdrop-blur-md border border-white/15 rounded-xl px-3 py-4 sm:py-5 text-center hover:bg-[#0B2B62]/75 hover:border-amber-300/50 transition-all">
                  <c.icon className={`w-6 h-6 sm:w-7 sm:h-7 mx-auto mb-2 ${c.color}`} strokeWidth={1.6} />
                  <div className="font-semibold text-white text-xs sm:text-sm">{c.title}</div>
                  <div className="text-[10px] sm:text-[11px] text-white/70 mt-1 leading-tight hidden sm:block">{c.desc}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Como funciona */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center mb-10">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Como funciona</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0B2B62] mt-2">Simples, humano e organizado</h2>
        </div>
        <div className="grid md:grid-cols-4 gap-5">
          {[
            { n: 1, t: "Cadastre seu familiar", d: "Você adiciona os dados básicos do beneficiário." },
            { n: 2, t: "Conte o que ele precisa", d: "Consulta, exame, fisioterapia, transporte..." },
            { n: 3, t: "Encontramos profissionais e serviços", d: "Central RAF conecta com a rede parceira." },
            { n: 4, t: "Acompanhe tudo pelo RAF Direct", d: "Agenda, histórico e status em um só lugar." },
          ].map((s) => (
            <div key={s.n} className="bg-white border border-slate-200 rounded-xl p-5 hover:border-[#0B2B62] transition-colors">
              <div className="w-9 h-9 rounded-full bg-[#0B2B62] text-white flex items-center justify-center font-bold text-sm mb-3">{s.n}</div>
              <div className="font-semibold text-slate-800 mb-1">{s.t}</div>
              <div className="text-sm text-slate-600 leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* O que ajudamos a organizar */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">O que ajudamos a organizar</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0B2B62] mt-2">Cuidado que cabe na rotina de todo mundo</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {[
              "Consultas", "Profissionais", "Clínicas", "Exames", "Fisioterapia",
              "Psicologia", "Nutrição", "Enfermagem", "Transporte", "Acompanhamento",
            ].map((item) => (
              <div key={item} className="bg-white border border-slate-200 rounded-lg px-4 py-4 text-center text-sm font-medium text-slate-700 hover:border-[#0B2B62] hover:shadow-sm transition-all">
                {item}
              </div>
            ))}
          </div>
          <div className="mt-8 text-center text-xs text-slate-500 max-w-2xl mx-auto">
            <Sparkles className="w-3.5 h-3.5 inline text-amber-500 mr-1" />
            Central de coordenação de cuidados no dia a dia. Precisando de <b>ILPI, casa assistida, casa de repouso ou residência permanente</b>?
            <Link to="/preciso-de-ajuda" className="text-[#0B2B62] font-semibold underline ml-1">A RAF Direct direciona pela Central de Emergência.</Link>
          </div>
        </div>
      </section>

      {/* Convite parceiros */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 rounded-2xl p-8 hover:border-[#0B2B62] transition-colors">
            <Stethoscope className="w-8 h-8 text-[#0B2B62] mb-4" />
            <div className="text-xl font-bold text-slate-800 mb-2">É profissional da saúde do idoso?</div>
            <p className="text-sm text-slate-600 mb-5">Cadastre-se e amplie sua atuação com famílias que buscam cuidado especializado.</p>
            <Link to="/filho-presente/cadastre-profissional" data-testid="fp-apply-pro"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B2B62] hover:underline">
              Quero me cadastrar <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 rounded-2xl p-8 hover:border-[#0B2B62] transition-colors">
            <Building2 className="w-8 h-8 text-[#0B2B62] mb-4" />
            <div className="text-xl font-bold text-slate-800 mb-2">Tem clínica, laboratório ou centro?</div>
            <p className="text-sm text-slate-600 mb-5">Torne-se parceiro Filho Presente e receba encaminhamentos qualificados.</p>
            <Link to="/filho-presente/cadastre-clinica" data-testid="fp-apply-clinic"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B2B62] hover:underline">
              Cadastrar clínica <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
